<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ActivitySummary from './ActivitySummary.vue';
import PlaygroundDrawer from '../playground/PlaygroundDrawer.vue';
import { usePlayground } from '../playground/usePlayground';

// Agent detail shell (D35): persistent header (Draft · vN · Publish) above a
// left rail segmented by speed — "Brain — versioned" vs "Live — applies
// instantly". D19: header shows display_name, never the sentinel email.
const props = defineProps({
  brain: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { isAdmin } = useAdmin();

const publishDialogRef = ref(null);
const changeNotes = ref('');

// P1-8: persistent playground slide-over, reachable from every tab.
const { toggle: togglePlayground } = usePlayground(props.brain.id);

const uiFlags = computed(
  () => store.getters['aiAgentBrains/getUIFlags'] || {}
);
const isPublishing = computed(() => uiFlags.value.publishingItem);

const versionLabel = computed(() =>
  props.brain.latest_version_number
    ? t('AI_AGENTS.AGENT.HEADER.VERSION', {
        version: props.brain.latest_version_number,
      })
    : t('AI_AGENTS.AGENT.HEADER.NO_VERSION')
);

const railSections = computed(() => [
  {
    label: t('AI_AGENTS.AGENT.RAIL.BRAIN_SECTION'),
    items: [
      {
        label: t('AI_AGENTS.AGENT.RAIL.BRAIN'),
        icon: 'i-lucide-brain',
        routeName: 'ai_agents_agent_brain',
      },
      {
        label: t('AI_AGENTS.AGENT.RAIL.MODEL'),
        icon: 'i-lucide-cpu',
        routeName: 'ai_agents_agent_model',
      },
      {
        label: t('AI_AGENTS.AGENT.RAIL.SKILLS'),
        icon: 'i-lucide-graduation-cap',
        routeName: 'ai_agents_agent_skills',
      },
      {
        label: t('AI_AGENTS.AGENT.RAIL.TOOLS'),
        icon: 'i-lucide-wrench',
        routeName: 'ai_agents_agent_tools',
      },
      {
        label: t('AI_AGENTS.AGENT.RAIL.VOICE'),
        icon: 'i-lucide-mic',
        routeName: 'ai_agents_agent_voice',
      },
      {
        label: t('AI_AGENTS.AGENT.RAIL.VERSIONS'),
        icon: 'i-lucide-history',
        routeName: 'ai_agents_agent_versions',
      },
      {
        label: t('AI_AGENTS.AGENT.RAIL.FLOW'),
        icon: 'i-lucide-git-branch',
        routeName: 'ai_agents_agent_flow',
      },
    ],
  },
  {
    label: t('AI_AGENTS.AGENT.RAIL.LIVE_SECTION'),
    items: [
      {
        label: t('AI_AGENTS.AGENT.RAIL.TICKETS'),
        icon: 'i-lucide-ticket',
        routeName: 'ai_agents_agent_tickets',
      },
      {
        label: t('AI_AGENTS.AGENT.RAIL.ROUTING'),
        icon: 'i-lucide-route',
        routeName: 'ai_agents_agent_live',
      },
    ],
  },
]);

const openPublishDialog = () => {
  changeNotes.value = '';
  publishDialogRef.value?.open();
};

const publish = async () => {
  try {
    const version = await store.dispatch('aiAgentBrains/publish', {
      id: props.brain.id,
      changeNotes: changeNotes.value,
    });
    useAlert(
      t('AI_AGENTS.AGENT.PUBLISH.SUCCESS', {
        version: version.version_number,
      })
    );
    publishDialogRef.value?.close();
  } catch (error) {
    if (error.response?.status === 422) {
      useAlert(
        t('AI_AGENTS.AGENT.PUBLISH.INVALID', {
          error: error.response.data?.error || '',
        })
      );
    } else {
      useAlert(t('AI_AGENTS.AGENT.PUBLISH.ERROR'));
    }
  }
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header class="px-6 pt-6 pb-4 border-b border-n-weak">
      <div class="w-full max-w-[70rem] mx-auto flex flex-col gap-2">
        <div class="flex flex-wrap items-center gap-3">
          <BackButton
            :back-url="{
              name: 'ai_agents_index',
              params: { accountId: route.params.accountId },
            }"
          />
          <Avatar :name="brain.display_name" :size="32" rounded-full />
          <span class="text-xl font-medium text-n-slate-12">
            {{ brain.display_name }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ t('AI_AGENTS.AGENT.HEADER.DRAFT') }} · {{ versionLabel }}
          </span>
          <div class="ltr:ml-auto rtl:mr-auto flex items-center gap-2">
            <Button
              :label="t('AI_AGENTS.PLAYGROUND.BUTTON')"
              icon="i-lucide-flask-conical"
              variant="outline"
              color="slate"
              size="sm"
              @click="togglePlayground"
            />
            <Button
              v-if="isAdmin"
              :label="t('AI_AGENTS.AGENT.HEADER.PUBLISH')"
              icon="i-lucide-upload"
              size="sm"
              @click="openPublishDialog"
            />
          </div>
        </div>
        <div
          v-if="brain.kill_switched"
          class="px-3 py-2 text-sm font-semibold rounded-lg bg-n-ruby-9 text-white"
        >
          {{ t('AI_AGENTS.AGENT.HEADER.KILL_SWITCHED_BANNER') }}
        </div>
        <ActivitySummary :brain-id="brain.id" />
      </div>
    </header>

    <div class="flex flex-1 min-h-0 w-full max-w-[70rem] mx-auto">
      <nav class="w-56 flex-shrink-0 px-4 py-4 overflow-y-auto">
        <div
          v-for="section in railSections"
          :key="section.label"
          class="mb-4"
        >
          <p
            class="px-2 mb-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ section.label }}
          </p>
          <ul class="flex flex-col gap-0.5 m-0 list-none">
            <li v-for="item in section.items" :key="item.routeName">
              <RouterLink
                :to="{
                  name: item.routeName,
                  params: {
                    accountId: route.params.accountId,
                    brainId: brain.id,
                  },
                }"
                class="flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg"
                :class="
                  route.name === item.routeName
                    ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                    : 'text-n-slate-11 hover:bg-n-alpha-1'
                "
              >
                <span :class="item.icon" class="size-4 flex-shrink-0" />
                {{ item.label }}
              </RouterLink>
            </li>
          </ul>
        </div>
      </nav>

      <main class="flex-1 min-w-0 px-6 py-4 overflow-y-auto">
        <slot />
      </main>
    </div>

    <Dialog
      ref="publishDialogRef"
      :title="t('AI_AGENTS.AGENT.PUBLISH.TITLE')"
      :description="t('AI_AGENTS.AGENT.PUBLISH.DESCRIPTION')"
      :confirm-button-label="t('AI_AGENTS.AGENT.PUBLISH.CONFIRM')"
      :cancel-button-label="t('AI_AGENTS.COMMON.CANCEL')"
      :is-loading="isPublishing"
      @confirm="publish"
    >
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('AI_AGENTS.AGENT.PUBLISH.NOTES_LABEL') }}
        <textarea
          v-model="changeNotes"
          rows="3"
          :placeholder="t('AI_AGENTS.AGENT.PUBLISH.NOTES_PLACEHOLDER')"
          class="px-3 py-2 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
        />
      </label>
    </Dialog>

    <PlaygroundDrawer :brain="brain" />
  </section>
</template>
